import { msg } from "@lingui/core/macro";
import { AlertCircle, RefreshCw } from "@fleet/icons";
import { type Component, Show } from "solid-js";

import { cn } from "@/lib/cn";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "./button";

interface ConnectionErrorStateProps {
  /** `data-testid` of the error box. Convention: `<screen>-connection-error`. */
  testId: string;
  /** Custom title. Default: «Could not load». */
  title?: string;
  /** Custom description. Default: explains that it is a connection failure. */
  description?: string;
  /**
   * Retry. It must really REQUEST again (`refetch()`), not reload the
   * page: a button that does not repeat the request is as deceitful as having
   * no error state. Its testid is `<testId>` + `-retry`.
   */
  onRetry?: () => void;
  class?: string;
}

/**
 * ERROR state of a remote load — the third state that almost never gets
 * written (P23 / CC-60, invariant I3).
 *
 * "No response" ≠ "there is nothing". Susurro is guest-first and local-first: its
 * lists (conversations, messages) come from the local state and cannot lie
 * because of a network failure. The exception is the **account** screen, which does
 * ask the relay: there, treating a failure as "you are a guest" offers to create
 * an account to someone who already has one, and suggests that they lost it.
 *
 * It deliberately does NOT reuse the empty state nor the neutral `GlassSurface`: it has to
 * look as LITTLE as possible like "there is nothing" (alert icon, destructive
 * border and an explicit retry).
 */
export const ConnectionErrorState: Component<ConnectionErrorStateProps> = (props) => {
  const { t } = useI18n();

  return (
    <div
      data-testid={props.testId}
      role="alert"
      class={cn(
        "flex flex-col items-center gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 p-6 text-center",
        props.class,
      )}
    >
      <div class="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertCircle class="h-5 w-5" />
      </div>
      <div class="flex flex-col gap-1">
        <p class="text-sm font-semibold text-foreground">
          {props.title ?? t(msg`Couldn't load this`)}
        </p>
        <p class="max-w-xs text-xs text-balance text-muted-foreground">
          {props.description ??
            t(msg`We couldn't reach the server. This is a connection problem, not empty data.`)}
        </p>
      </div>
      <Show when={props.onRetry}>
        {(retry) => (
          <Button
            variant="outline"
            size="sm"
            class="gap-2"
            data-testid={`${props.testId}-retry`}
            onClick={() => {
              retry()();
            }}
          >
            <RefreshCw class="h-3.5 w-3.5" />
            {t(msg`Retry`)}
          </Button>
        )}
      </Show>
    </div>
  );
};
